<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import type { DisorderTemplate, DisorderGrade } from "@/types/catalogues.types";
import DisorderGradeBadge from "@/components/UI/DisorderGradeBadge.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import { getGradeOrder } from "@/constants/notationSystems";

const props = withDefaults(
  defineProps<{
    disorder: DisorderTemplate;
    isExpanded?: boolean;
    selectedGradeId?: number;
  }>(),
  {
    isExpanded: false,
  },
);

const emit = defineEmits<{
  (e: "toggle"): void;
  (e: "selectGrade", disorder: DisorderTemplate, grade: DisorderGrade): void;
  (e: "selectDisorder", disorder: DisorderTemplate): void;
}>();

const { t } = useTranslation();

const hasGrades = computed(() => props.disorder.grades?.length > 0);

const toggleExpand = () => {
  if (hasGrades.value) {
    emit("toggle");
  }
};

const selectGrade = (grade: DisorderGrade) => {
  emit("selectGrade", props.disorder, grade);
};

// Tri par gravité croissante, quel que soit le système de notation du catalogue.
// Un désordre peut porter plusieurs grades de même note (un par critère) : le tri
// est stable, ils restent donc dans l'ordre du catalogue.
const sortedGrades = computed(() =>
  [...(props.disorder.grades ?? [])].sort(
    (a, b) => getGradeOrder(a.class) - getGradeOrder(b.class),
  ),
);
</script>

<template>
  <div
    class="disorder-item"
    :class="{
      expanded: isExpanded,
      'has-grades': hasGrades,
    }"
    :data-disorder-id="disorder.id"
  >
    <!-- Header row (clickable to expand) -->
    <div class="disorder-header" @click="toggleExpand">
      <!-- Content -->
      <div class="disorder-content">
        <div class="disorder-title">
          <span class="disorder-code">{{ disorder.code }}</span>
          <span class="disorder-name">{{
            disorder.title || t("report:diagnostics.noname")
          }}</span>
        </div>
        <div v-if="disorder.description" class="disorder-description mt-1">
          {{ disorder.description }}
        </div>
      </div>

      <!-- Grades preview and expand icon -->
      <div v-if="hasGrades" class="d-flex align-items-center gap-2 mt-2">
        <div class="grades-preview d-flex gap-1 flex-shrink-0">
          <DisorderGradeBadge
            v-for="grade in sortedGrades"
            :key="grade.id"
            :value="grade.class"
            size="sm"
          />
        </div>
        <div class="expand-icon">
          <i
            class="bi"
            :class="isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'"
          ></i>
        </div>
      </div>

      <!-- Apply disorder button (no grades) -->
      <div v-else class="d-flex justify-content-start mt-2">
        <DRBtn
          icon="bi-clipboard-check"
          class="text-warning"
          :label="t('report:findings.catalogueTab.useDisorder')"
          size="xs"
          variant="flat"
          @click.stop="emit('selectDisorder', disorder)"
        />
      </div>
    </div>

    <!-- Grades list (expanded) -->
    <div v-if="isExpanded && hasGrades" class="grades-section">
      <div class="grades-instruction">
        {{ t("report:findings.catalogueTab.selectGradeInstruction") }}
      </div>
      <div class="grades-list">
        <div
          v-for="grade in sortedGrades"
          :key="grade.id"
          class="grade-item"
          :class="{ selected: selectedGradeId === grade.id }"
          @click="selectGrade(grade)"
        >
          <div class="grade-badge-col">
            <DisorderGradeBadge :value="grade.class" size="md" />
          </div>
          <div
            class="grade-description-col"
            :class="{ 'text-muted': !grade.description }"
          >
            {{ grade.description || t("report:catalogue.grade.noDescription") }}
          </div>
          <i
            v-if="selectedGradeId === grade.id"
            class="bi bi-check-lg check-icon"
          ></i>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.disorder-item {
  background-color: var(--bs-white);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.15s ease;
  border: 1px solid var(--catalogue-border);

  &.has-grades {
    cursor: pointer;
  }

  &:hover {
    background-color: var(--catalogue-hover);
  }
}

.disorder-header {
  padding: 12px;
}

.grades-preview {
  flex-wrap: wrap;
}

.expand-icon {
  width: 16px;
  color: var(--catalogue-text);
  font-size: 12px;
}

.disorder-content {
  color: var(--bs-dark);
}

.disorder-title {
  font-size: 14px;
  line-height: 1.3;
}

.disorder-code {
  color: var(--catalogue-text);
  font-weight: 600;
  margin-right: 6px;
}

.disorder-name {
  color: var(--bs-dark);
  text-transform: none;
}

.disorder-description {
  font-size: 12px;
  color: var(--bs-secondary);
  line-height: 1.4;
  white-space: pre-line;
}

.grades-section {
  border-top: 1px solid var(--catalogue-border);
  background-color: var(--bs-white);
}

.grades-instruction {
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  color: var(--catalogue-text);
  background-color: var(--catalogue-nav);
}

.grades-list {
  padding: 8px;
  background-color: var(--bs-white);
}

.grade-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--bs-gray-100);
  }

  &.selected {
    background-color: rgba(25, 135, 84, 0.15);
  }
}

.grade-badge-col {
  flex-shrink: 0;
  width: 40px;
}

.grade-description-col {
  flex: 1;
  font-size: 13px;
  color: var(--bs-dark);
  line-height: 1.4;
  white-space: pre-line;
}

.check-icon {
  color: var(--bs-success);
  font-size: 16px;
  flex-shrink: 0;
}

.use-disorder-btn {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px 10px;
  border: none;
  border-radius: 6px;
  background-color: transparent;
  color: var(--catalogue-text);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: var(--catalogue-hover);
    color: var(--bs-dark);
  }
}
</style>
